import { getColumnPreferences, saveColumnPreferences } from '../api';
import { getDefaultColumns } from '../config/columnDefaults';

const STORAGE_KEY = 'crm_column_prefs_v1';
const PREFS_VERSION = 1;

/**
 * Read all column prefs from localStorage.
 */
function readLocalStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/**
 * Write all column prefs to localStorage.
 */
function writeLocalStorage(allPrefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allPrefs));
  } catch {
    // localStorage full or unavailable — silent fail
  }
}

/**
 * Merge saved columns with current defaults.
 * Handles cases where columns have been added or removed since prefs were saved.
 */
function mergeWithDefaults(savedColumns, entityType) {
  const defaults = getDefaultColumns(entityType);
  if (!savedColumns || savedColumns.length === 0) return defaults;

  const savedMap = new Map(savedColumns.map(c => [c.key, c]));
  const defaultMap = new Map(defaults.map(c => [c.key, c]));
  const merged = [];

  // Keep saved columns that still exist in defaults, preserving saved order/visibility
  savedColumns.forEach(saved => {
    const def = defaultMap.get(saved.key);
    if (def) {
      merged.push({
        key: saved.key,
        label: def.label, // always use current label
        visible: def.locked ? true : saved.visible,
        order: saved.order,
        locked: def.locked,
      });
    }
    // If a saved column no longer exists in defaults, drop it
  });

  // Add any new default columns that weren't in saved prefs
  defaults.forEach(def => {
    if (!savedMap.has(def.key)) {
      merged.push({
        ...def,
        order: merged.length, // append to end
      });
    }
  });

  return merged;
}

/**
 * Get column preferences for an entity type.
 * Tries API first, falls back to localStorage.
 */
async function getPreferences(entityType) {
  // Try API
  try {
    const response = await getColumnPreferences(entityType);
    if (response.data && response.data.columns && response.data.columns.length > 0) {
      const columns = mergeWithDefaults(response.data.columns, entityType);
      // Cache in localStorage
      const allPrefs = readLocalStorage();
      allPrefs[entityType] = { columns, version: PREFS_VERSION, lastUpdated: new Date().toISOString() };
      writeLocalStorage(allPrefs);
      return columns;
    }
  } catch {
    // API failed — try localStorage
  }

  // Fallback: localStorage
  const allPrefs = readLocalStorage();
  const saved = allPrefs[entityType];
  if (saved && saved.columns && saved.columns.length > 0) {
    return mergeWithDefaults(saved.columns, entityType);
  }

  // No saved prefs — return defaults
  return getDefaultColumns(entityType);
}

/**
 * Save column preferences for an entity type.
 * Saves to API + localStorage. If API fails, still saves to localStorage.
 */
async function savePreferences(entityType, columns) {
  const prefs = {
    columns,
    version: PREFS_VERSION,
    lastUpdated: new Date().toISOString(),
  };

  // Always save to localStorage
  const allPrefs = readLocalStorage();
  allPrefs[entityType] = prefs;
  writeLocalStorage(allPrefs);

  // Try API (non-blocking failure)
  try {
    await saveColumnPreferences(entityType, columns);
  } catch {
    // localStorage is our fallback — already saved above
  }
}

/**
 * Reset column preferences to defaults for an entity type.
 */
async function resetToDefaults(entityType) {
  const defaults = getDefaultColumns(entityType);
  await savePreferences(entityType, defaults);
  return defaults;
}

export { getPreferences, savePreferences, resetToDefaults, mergeWithDefaults };
